import { useState, useRef, useEffect, useLayoutEffect, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import cn from "mcn";
import { useNewFeatures } from "../../sanity/queries";
import { AnnouncementBody, getAnnouncementImageUrls } from "../infoSection/Announcement";
import {
  insertViewedAnnouncement,
  useViewedAnnouncements,
  getRecentFeatures,
  type WhatsNewFeature,
} from "../infoSection/hooks";
import { useGraphQLClient } from "@package/graphql/react";
import { useQueryClient } from "@tanstack/react-query";
import { useUserId } from "../../context/auth/hooks";
import { Button } from "../ui/button";
import { CloseIcon } from "../icons/CloseIcon";

const MAX_WHATS_NEW = 5;

export const useWhatsNewCount = () => {
  const features = useNewFeatures();
  const viewedAnnouncements = useViewedAnnouncements();
  return getRecentFeatures(features.data?.allNewFeature).filter(
    (feature) => !viewedAnnouncements.data?.some((row) => row.announcement_id === feature._id && row.viewed_at != null),
  ).length;
};

const WINDOW_WIDTH = 640;
const WINDOW_MAX_HEIGHT_RATIO = 0.8;

const clampPos = (x: number, y: number, w: number, h: number) => ({
  x: Math.max(0, Math.min(x, window.innerWidth - w)),
  y: Math.max(0, Math.min(y, window.innerHeight - h)),
});

const centerPos = (w: number, h: number) => ({
  x: Math.max(0, (window.innerWidth - w) / 2),
  y: Math.max(0, (window.innerHeight - h) / 2),
});

export const WhatsNewModal = memo(function WhatsNewModal({
  feature,
  onClose,
}: {
  feature: WhatsNewFeature;
  onClose: () => void;
}) {
  const [pos, setPos] = useState(() => {
    const estimatedHeight = Math.min(600, window.innerHeight * WINDOW_MAX_HEIGHT_RATIO);
    return centerPos(WINDOW_WIDTH, estimatedHeight);
  });

  const windowRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasBeenDragged = useRef(false);

  // useLayoutEffect so the estimated initial position is corrected before paint, instead of
  // visibly snapping to the real position a frame after appearing.
  useLayoutEffect(() => {
    const node = windowRef.current;
    if (!node) return;

    const reposition = () => {
      const { offsetWidth, offsetHeight } = node;
      setPos((prev) =>
        hasBeenDragged.current
          ? clampPos(prev.x, prev.y, offsetWidth, offsetHeight)
          : centerPos(offsetWidth, offsetHeight),
      );
    };

    reposition();

    const resizeObserver = new ResizeObserver(reposition);
    resizeObserver.observe(node);
    window.addEventListener("resize", reposition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", reposition);
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      hasBeenDragged.current = true;
      const { offsetWidth = WINDOW_WIDTH, offsetHeight = 600 } = windowRef.current ?? {};
      setPos(clampPos(e.clientX - dragOffset.current.x, e.clientY - dragOffset.current.y, offsetWidth, offsetHeight));
    };
    const onMouseUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleDragStart = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  return createPortal(
    <div
      ref={windowRef}
      data-curve-mgmt-tokens
      className="fixed z-[9999] flex max-h-[80vh] w-[640px] max-w-[calc(100vw-432px)] flex-col overflow-hidden rounded-[8px] border border-[var(--colour-border-default)] bg-[var(--colour-surface-container-default)] shadow-2xl"
      style={{ top: pos.y, left: pos.x }}
    >
      <div
        className="flex shrink-0 cursor-grab active:cursor-grabbing select-none flex-col gap-[4px] px-[24px] pt-[16px] pb-[16px]"
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-[24px]">
          <h2 className="min-w-0 flex-1 text-[18px] font-medium leading-[24px] tracking-[0.4px] text-[var(--colour-text-default)]">
            {feature.title}
          </h2>
          <CloseIcon onClick={onClose} />
        </div>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-[24px] pb-4">
        <div className="flex flex-col gap-[16px]">
          <AnnouncementBody bodyRaw={feature.bodyRaw} variant="modal" />
        </div>
      </div>

      <div className="flex shrink-0 justify-end px-[24px] pb-4 pt-1">
        <Button buttonType="closeSecondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>,
    document.body,
  );
});

/** Caps how long we wait for images before opening the modal anyway (e.g. a stalled request while offline). */
const PRELOAD_TIMEOUT_MS = 4000;

/** Resolves once every image in the body has loaded (or failed to), so the modal opens with all of its content already available instead of popping in piecemeal. */
const preloadAnnouncementImages = (bodyRaw: unknown): Promise<void> => {
  const urls = getAnnouncementImageUrls(bodyRaw);

  if (urls.length === 0) {
    return Promise.resolve();
  }

  const allLoaded = Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = url;
        }),
    ),
  ).then(() => undefined);

  const timeout = new Promise<void>((resolve) => setTimeout(resolve, PRELOAD_TIMEOUT_MS));

  return Promise.race([allLoaded, timeout]);
};

const ReadMoreButton = ({ onClick, viewed, loading }: { onClick: () => void; viewed: boolean; loading: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className={cn(
      "inline-flex shrink-0 items-center justify-center rounded-[8px] px-[8px] py-[4px] min-h-[30px] text-[14px] font-medium leading-[20px] capitalize",
      [viewed, "text-[var(--colour-text-neutral-default)]", "text-[var(--colour-text-primary-default)]"],
      [loading, "opacity-60 cursor-default", "cursor-pointer"],
    )}
  >
    Read more
  </button>
);

const FeatureCard = ({
  feature,
  isOpen,
  onOpen,
  onClose,
}: {
  feature: WhatsNewFeature;
  /** Only one card's modal can be open at a time — this is whether it's *this* card's. */
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const [isPreparing, setIsPreparing] = useState(false);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const userId = useUserId();
  const hasura = useGraphQLClient();
  const queryClient = useQueryClient();
  const viewedAnnouncements = useViewedAnnouncements();
  const isViewed = Boolean(
    viewedAnnouncements.data?.some((row) => row.announcement_id === feature._id && row.viewed_at != null),
  );

  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const openModalAndMarkViewed = () => {
    onOpen();
    if (feature._id && feature.title) {
      insertViewedAnnouncement(
        userId,
        {
          title: feature.title,
          announcement_id: feature._id,
          viewed_at: new Date().toISOString(),
        },
        viewedAnnouncements.data ?? [],
        hasura,
        queryClient,
      );
    }
  };

  const handleReadMore = async () => {
    if (isOpen || isPreparing) {
      return;
    }

    setIsPreparing(true);
    try {
      await preloadAnnouncementImages(feature.bodyRaw);
    } catch {
      // Best-effort — open the modal regardless of preload failures.
    }
    if (!isMounted.current) {
      return;
    }
    setIsPreparing(false);
    openModalAndMarkViewed();
  };

  return (
    <div
      className={cn("flex w-full shrink-0 overflow-hidden rounded-[8px] border px-[16px] py-[12px]", [
        isViewed,
        "border-[var(--colour-border-default)]",
        "border-[var(--colour-border-dark)] bg-[var(--colour-surface-container-variant)]",
      ])}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[12px]">
        <div className="flex w-full min-w-0 items-center gap-[4px]">
          <p className="min-w-0 flex-1 truncate text-[14px] font-medium leading-[20px] text-[var(--colour-text-default)]">
            {feature.title}
          </p>
          <ReadMoreButton onClick={handleReadMore} viewed={isViewed} loading={isPreparing} />
        </div>
        {feature.description ? (
          <p className="min-w-0 truncate text-[14px] font-normal leading-[20px] text-[var(--colour-text-default)]">
            {feature.description}
          </p>
        ) : null}
      </div>
      {isOpen && <WhatsNewModal feature={feature} onClose={handleCloseModal} />}
    </div>
  );
};

export const WhatsNewAccordion = () => {
  const features = useNewFeatures();
  const [openFeatureId, setOpenFeatureId] = useState<string | null>(null);

  const recentFeatures = getRecentFeatures(features.data?.allNewFeature).slice(0, MAX_WHATS_NEW);

  if (features.loading) {
    return (
      <div className="px-[8px] py-[4px] text-[14px] font-normal leading-[20px] text-[var(--colour-text-disabled)]">
        Loading...
      </div>
    );
  }

  if (!recentFeatures || recentFeatures.length === 0) {
    return (
      <div className="px-[8px] py-[4px] text-[14px] font-normal leading-[20px] text-[var(--colour-text-disabled)]">
        No announcements yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[8px] px-[8px] py-[4px]">
      {recentFeatures.map((feature) => (
        <FeatureCard
          key={feature._id}
          feature={feature}
          isOpen={feature._id != null && openFeatureId === feature._id}
          onOpen={() => setOpenFeatureId(feature._id ?? null)}
          onClose={() => setOpenFeatureId((current) => (current === feature._id ? null : current))}
        />
      ))}
    </div>
  );
};
